import type { FC } from "react";
import { useMemo, useState } from "react";

import { DEPRECATED_Loading } from "@ui/components/DEPRECATED_Loading";
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTrigger,
} from "@ui/components/Drawer";
import { I18n } from "@ui/components/I18n";
import type { BrandWithFlavorItems } from "@ui/components/MultibrandMultipleFlavorsSelector";
import { MultibrandMultipleFlavorSelectorToggler } from "@ui/components/MultibrandMultipleFlavorsSelector/components/MultibrandMultipleFlavorSelectorToggler";
import { MultipleFlavorSelectorSticksBlock } from "@ui/components/MultipleFlavorsSelector/components/MultipleFlavorSelectorSticksBlock";
import { useFlavorsSelectionController } from "@ui/hooks/useFlavorsSelectionController";
import { useTranslations } from "@ui/providers/useTranslations";
import classnames from "classnames";

import { Box } from "@jti/core-components/components/Box";
import { Button } from "@jti/core-components/components/Button";
import type { FlavorsSelection, StockStatusElements } from "@jti/fe-common";

import styles from "./MultibrandMultipleFlavorsSelector.module.scss";

type MultibrandMultipleFlavorsSelectorProps = {
  brands: BrandWithFlavorItems[];
  stockStatusElements: StockStatusElements;
  onChange: (flavorSelection: FlavorsSelection) => void;
  title: string;
  limit: number;
  defaultFlavorsSelection?: FlavorsSelection | null;
  showValidation?: boolean;
  hasError?: boolean;
  isProductDataLoading?: boolean;
  error?: string;
};

export const MultibrandMultipleFlavorsSelector: FC<MultibrandMultipleFlavorsSelectorProps> = ({
  brands,
  stockStatusElements,
  onChange,
  title,
  limit,
  defaultFlavorsSelection,
  showValidation = false,
  hasError = false,
  isProductDataLoading = false,
  error,
}) => {
  const { __ } = useTranslations();

  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);

  const {
    flavorSelection,
    selectedPacksCount,
    isLimitReached,
    temporarySelection,
    temporarySelectedPacksCount,
    isTemporaryLimitReached,
    hasValidationError,
    setTemporarySelection,
    onCollectionChange,
    onTemporaryCollectionChange,
    onDrawerApplySelection,
  } = useFlavorsSelectionController({
    limit,
    defaultFlavorsSelection,
    onChange,
    validate: showValidation,
    hasError,
  });

  const onTogglerClick = (brandId: string) => {
    if (selectedBrandId === brandId) {
      setSelectedBrandId(null);
    } else {
      setSelectedBrandId(brandId);
    }
  };

  const flatItemsWithBrand = useMemo(
    () =>
      brands.flatMap((brand) =>
        brand.items.map((item) => ({
          ...item,
          brandId: brand.brandId,
          brandLabel: brand.brandLabel,
          brandUrl: brand.brandIconUrl,
        }))
      ),
    [brands]
  );

  const getSelectedProductsForBrand = (brandId: string, selection: FlavorsSelection) => {
    return flatItemsWithBrand
      .filter((i) => i.brandId === brandId && !!selection[i.id])
      .map((i) => ({ ...i, count: selection[i.id] }));
  };

  const selectedBrandItems = useMemo(() => {
    if (!selectedBrandId) {
      return [];
    }

    return brands.find((brand) => brand.brandId === selectedBrandId)?.items ?? [];
  }, [selectedBrandId, brands]);

  return (
    <>
      <div
        className={classnames(styles.container, hasValidationError && styles.hasError)}
        data-testid="MultibrandMultipleFlavorsSelectorContainer"
      >
        <Box title={`${title} ${selectedPacksCount}/${limit}`}>
          {isProductDataLoading && <DEPRECATED_Loading />}
          <div className={styles.desktopView} data-testid="MultipleFlavorsSelector-desktopView">
            <DrawerRoot onCancelCallback={() => setSelectedBrandId(null)}>
              <DrawerTrigger>
                <div className={styles.togglers}>
                  {brands.map((brand) => (
                    <MultibrandMultipleFlavorSelectorToggler
                      key={brand.brandId}
                      onClick={() => {
                        setTemporarySelection(flavorSelection);
                        setSelectedBrandId(brand.brandId);
                      }}
                      brandLabel={brand.brandLabel}
                      brandImgUrl={brand.brandIconUrl}
                      isSelectorOpen={selectedBrandId === brand.brandId}
                      selectedBrandItems={getSelectedProductsForBrand(brand.brandId, flavorSelection)}
                    />
                  ))}
                </div>
              </DrawerTrigger>

              <DrawerContent aria-label={__("productdetails.sticks.drawerHeadline")}>
                <DrawerHeader className={styles.drawerHeader}>
                  {__("productdetails.sticks.drawerHeadline")}
                  <div className={styles.drawerSubheader} aria-live="polite">
                    <I18n t="productdetails.sticks.selection">
                      <strong>{temporarySelectedPacksCount}</strong>
                      {limit}
                    </I18n>
                  </div>

                  <div className={classnames(styles.togglers, styles.drawer)}>
                    {brands.map((brand) => (
                      <MultibrandMultipleFlavorSelectorToggler
                        key={brand.brandId}
                        onClick={() => setSelectedBrandId(brand.brandId)}
                        brandLabel={brand.brandLabel}
                        brandImgUrl={brand.brandIconUrl}
                        isSelectorOpen={selectedBrandId === brand.brandId}
                        selectedBrandItems={getSelectedProductsForBrand(brand.brandId, temporarySelection)}
                        hideOverview={true}
                      />
                    ))}
                  </div>
                </DrawerHeader>

                <DrawerBody>
                  {selectedBrandId !== null && (
                    <ul className={styles.sticksList}>
                      {selectedBrandItems.map((item) => {
                        return (
                          <li key={item.id} className={styles.stickItem}>
                            <MultipleFlavorSelectorSticksBlock
                              label={item.name ?? ""}
                              description={item?.description ?? ""}
                              id={item.id}
                              imageUrl={item.image.url ?? ""}
                              stockStatus={stockStatusElements[item.id]}
                              initialQuantity={temporarySelection[item.id] || 0}
                              isLimitReached={isTemporaryLimitReached}
                              onChange={onTemporaryCollectionChange}
                              isStockStatusLoading={isProductDataLoading}
                              attributes={item.attributes ?? []}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </DrawerBody>

                <DrawerFooter>
                  <DrawerCloseTrigger>
                    <Button
                      onClick={() => onDrawerApplySelection(() => setSelectedBrandId(null))}
                      variant="primary"
                      data-testid="applySelectionButton"
                    >
                      {__("productdetails.sticks.applySelection")}
                    </Button>
                  </DrawerCloseTrigger>
                </DrawerFooter>
              </DrawerContent>

              <DrawerBackdrop />
            </DrawerRoot>
          </div>

          <div className={styles.mobileView} data-testid="MultipleFlavorsSelector-mobileView">
            <div className={styles.togglers}>
              {brands.map((brand) => (
                <MultibrandMultipleFlavorSelectorToggler
                  key={brand.brandId}
                  onClick={() => onTogglerClick(brand.brandId)}
                  brandLabel={brand.brandLabel}
                  brandImgUrl={brand.brandIconUrl}
                  isSelectorOpen={selectedBrandId === brand.brandId}
                  selectedBrandItems={getSelectedProductsForBrand(brand.brandId, flavorSelection)}
                />
              ))}
            </div>

            {selectedBrandId !== null && (
              <section>
                <ul className={styles.sticksList}>
                  {selectedBrandItems.map((item) => {
                    return (
                      <li key={item.id} className={styles.stickItem}>
                        <MultipleFlavorSelectorSticksBlock
                          label={item.name ?? ""}
                          description={item.description ?? ""}
                          id={item.id}
                          isLimitReached={isLimitReached}
                          initialQuantity={flavorSelection[item.id] || 0}
                          imageUrl={item.image.url ?? ""}
                          stockStatus={stockStatusElements[item.id]}
                          onChange={onCollectionChange}
                          isStockStatusLoading={isProductDataLoading}
                          attributes={item.attributes ?? []}
                        />
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </div>
        </Box>
      </div>

      {hasValidationError && <div className={styles.error}>{__("productdetails.sticks.validationError")}</div>}

      {error && <div className={styles.error}>{error}</div>}
    </>
  );
};
